// The unfilled-placeholder highlight, shared by every reply box (2026-10-26,
// docs/contact-view.md, "The agent draft panel"): the email editor
// (RichReplyEditor.jsx, a ProseMirror decoration), the plain-text boxes
// (PlaceholderOverlay.jsx, a layer drawn over the textarea) and the admin
// agent draft column's read-only reply (AgentDraftLayout.jsx). Display only:
// nothing here ever changes the text that is sent.
//
// What is highlighted: the INSERT tokens of @cxhub/shared's
// findUnresolvedPlaceholders() (one pattern, UNRESOLVED_PLACEHOLDER_SOURCE,
// digits included, e.g. INSERT5THDATE), the XXX fill-ins and (2026-10-26)
// a carrier pick-one list ("Carrier: UPS / USPS / ATS", carrierPickList(),
// the line findUnresolvedPlaceholders() reports): the list itself, not its
// label. The pipeline and the agent set that line to the order's one
// carrier when they can tell it (server/src/ai/carrier-choice.js); one left
// is for the agent to choose.
import { UNRESOLVED_PLACEHOLDER_SOURCE, CARRIER_PICK_LIST_SOURCE, carrierPickList, RMA_REFERENCE_TOKEN } from '@cxhub/shared'

export const PLACEHOLDER_HIGHLIGHT_SOURCE = `${UNRESOLVED_PLACEHOLDER_SOURCE}|(?<![A-Za-z0-9])X{3,}(?![A-Za-z0-9])`

// The email editor's colors (RichReplyEditor.jsx's .reply-placeholder).
export const PLACEHOLDER_HIGHLIGHT_BG = '#FFF1C9'
export const PLACEHOLDER_HIGHLIGHT_TEXT = '#6B4200'
// Dark mode, for the plain-text boxes only: their text keeps the box's own
// light color (the layer cannot recolor it), so the fill is a translucent
// amber that light text stays readable on.
export const PLACEHOLDER_HIGHLIGHT_BG_DARK = 'rgba(255, 193, 7, 0.32)'

// The text split into runs, in order: { text, placeholder }. Joined back, the
// runs are exactly the text given.
export function placeholderSegments(text) {
  const s = String(text ?? '')
  const out = []
  let last = 0
  for (const [from, to] of placeholderRanges(s)) {
    if (from > last) out.push({ text: s.slice(last, from), placeholder: false })
    out.push({ text: s.slice(from, to), placeholder: true })
    last = to
  }
  if (last < s.length) out.push({ text: s.slice(last), placeholder: false })
  return out
}

// A carrier pick-one line: an optional short label, then the list as the
// line's whole value (the shape of @cxhub/shared's carrierPickList()).
// Group 1 is everything before the list, group 2 the list.
const CARRIER_LINE_RE = new RegExp(String.raw`^(\s*(?:[^:\n]{0,60}:\s*)?)(${CARRIER_PICK_LIST_SOURCE})\s*\.?\s*$`, 'i')

// The carrier pick-one lists in the text as [start, end) offsets: per line
// (split at newlines), only where carrierPickList() says the line is one.
export function carrierPickRanges(text) {
  const s = String(text ?? '')
  const out = []
  let at = 0
  for (const line of s.split('\n')) {
    if (carrierPickList(line)) {
      const m = CARRIER_LINE_RE.exec(line)
      if (m) out.push([at + m[1].length, at + m[1].length + m[2].length])
    }
    at += line.length + 1
  }
  return out
}

// Every highlighted run as [start, end) offsets, in order and never
// overlapping: the INSERT and XXX tokens, and the carrier pick-one lists.
export function placeholderRanges(text) {
  const s = String(text ?? '')
  const ranges = []
  const re = new RegExp(PLACEHOLDER_HIGHLIGHT_SOURCE, 'g')
  let m
  while ((m = re.exec(s)) !== null) ranges.push([m.index, m.index + m[0].length])
  ranges.push(...carrierPickRanges(s))
  ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1])
  const out = []
  for (const r of ranges) {
    const last = out[out.length - 1]
    if (last && r[0] < last[1]) { last[1] = Math.max(last[1], r[1]); continue }
    out.push([...r])
  }
  return out
}

export function hasPlaceholderHighlight(text) {
  const s = String(text ?? '')
  return new RegExp(PLACEHOLDER_HIGHLIGHT_SOURCE).test(s) || carrierPickRanges(s).length > 0
}

// The email editor's decoration ranges (RichReplyEditor.jsx), from a
// ProseMirror document: per textblock, its inline content as one string, a
// hard break as "\n" and any other inline leaf as one placeholder character,
// so a string offset plus the block's content start is the document
// position. A token split by a mark (part bold) is still found. Returns
// [{ from, to }] in document positions. Display only: the document is read,
// never changed.
export function placeholderDocRanges(doc) {
  const out = []
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true
    let text = ''
    node.forEach((child) => {
      if (child.isText) text += child.text
      else text += child.type.name === 'hardBreak' || child.type.name === 'hard_break' ? '\n' : '￼'.repeat(child.nodeSize)
    })
    for (const [from, to] of placeholderRanges(text)) out.push({ from: pos + 1 + from, to: pos + 1 + to })
    return false
  })
  return out
}

// The RMA number's token (2026-10-27, @cxhub/shared's rmartn-cust-pays.js),
// drawn as a chip in the email editor: its ranges, in document positions,
// the same walk as placeholderDocRanges().
export function rmaChipDocRanges(doc) {
  const out = []
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true
    let text = ''
    node.forEach((child) => {
      if (child.isText) text += child.text
      else text += child.type.name === 'hardBreak' || child.type.name === 'hard_break' ? '\n' : '￼'.repeat(child.nodeSize)
    })
    let at = text.indexOf(RMA_REFERENCE_TOKEN)
    while (at >= 0) {
      out.push({ from: pos + 1 + at, to: pos + 1 + at + RMA_REFERENCE_TOKEN.length })
      at = text.indexOf(RMA_REFERENCE_TOKEN, at + RMA_REFERENCE_TOKEN.length)
    }
    return false
  })
  return out
}

// The textarea's computed styles the layer copies, so its text wraps and
// lines up character for character with the textarea's.
export const MIRRORED_STYLE_PROPS = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant', 'fontStretch', 'fontKerning', 'fontFeatureSettings',
  'letterSpacing', 'wordSpacing', 'lineHeight', 'textTransform', 'textIndent', 'textAlign', 'tabSize', 'direction',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'whiteSpace', 'overflowWrap', 'wordBreak',
]

// The layer's text: what the textarea shows. A textarea shows a CR LF as one
// line break, and a trailing line break still makes a line, so the layer
// ends with a space to give that line its height too.
export function mirrorText(value) {
  return `${String(value ?? '').replace(/\r\n?/g, '\n')} `
}
